import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { api } from '../lib/api';
import ClassPhoto from '../components/ClassPhoto';

const MEMBERSHIPS = [
  {
    name: 'Open Gym',
    price: '£59.99',
    period: '/month',
    colour: '#1A1A1A',
    features: ['Unlimited open gym access', 'All equipment', 'Flexible session times', 'InBody 770 scan included', 'Member app access'],
    badge: null,
  },
  {
    name: 'Gold',
    price: '£89.99',
    period: '/month',
    colour: '#FF6200',
    features: ['Everything in Open Gym', 'All strength classes', 'All cardio classes', 'Mobility & function classes', 'InBody 770 scan included'],
    badge: 'Best Value',
  },
  {
    name: 'Junior',
    price: '£39.99',
    period: '/month',
    colour: '#1565C0',
    features: ['Full gym access', 'Age-appropriate programming', 'Group training included', 'InBody 770 scan included', 'Youth development focus'],
    badge: 'Under 18s',
  },
  {
    name: 'Couples',
    price: '£159.99',
    period: '/month',
    colour: '#6A1B9A',
    features: ['2× full Gold memberships', 'Independent booking accounts', 'All group classes included', 'InBody 770 scan for both'],
    badge: 'Two Members',
  },
];

const CLASSES = [
  { name: 'Strong',      type: 'Strength' },
  { name: 'Vortex',     type: 'Strength' },
  { name: 'Armageddon', type: 'Strength' },
  { name: 'Kettlebells', type: 'Strength' },
  { name: 'Spin',       type: 'Cardio'   },
  { name: 'Burn',       type: 'Cardio'   },
  { name: 'Box',        type: 'Cardio'   },
  { name: 'FIT',        type: 'Cardio'   },
  { name: 'Fusion',     type: 'Cardio'   },
  { name: 'Core',       type: 'Mobility' },
  { name: 'Balance',    type: 'Mobility' },
  { name: 'Stretch & Core', type: 'Mobility' },
  { name: 'Spine',      type: 'Mobility' },
  { name: 'Yoga',       type: 'Mobility' },
  { name: 'Flow',        type: 'Mobility' },
];

const TYPE_COLOURS = { Strength: '#FF6200', Cardio: '#D32F2F', Mobility: '#1565C0' };

// Heuristic to colour-code a class card by its name (best-effort match against
// our known list of class types). Falls back to neutral if unknown.
function pickClassColour(className) {
  if (!className) return '#1A1A1A';
  const lower = className.toLowerCase();
  const match = CLASSES.find(c => lower.includes(c.name.toLowerCase()));
  return match ? TYPE_COLOURS[match.type] : '#1A1A1A';
}

// UK-local date formatter
const UK_DAY = new Intl.DateTimeFormat('en-GB', { weekday: 'short', day: '2-digit', month: 'short', timeZone: 'Europe/London' });
const UK_TIME = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Europe/London' });

function formatDayLabel(iso) {
  if (!iso) return '';
  return UK_DAY.format(new Date(iso));
}
function formatTime(iso) {
  if (!iso) return '';
  return UK_TIME.format(new Date(iso));
}
function dayKey(iso) {
  if (!iso) return '';
  return UK_DAY.format(new Date(iso));
}

export default function FitnessPage() {
  const { session } = useAuth();
  const nav = useNavigate();
  const email = session?.user?.email || '';
  const teamupBase = 'https://goteamup.com/p/1728390-team-breakthrough/';
  const joinUrl = email ? `${teamupBase}memberships/?email=${encodeURIComponent(email)}` : `${teamupBase}memberships/`;

  // Class timetable state
  const [classes, setClasses] = useState([]);
  const [loadingClasses, setLoadingClasses] = useState(true);
  const [classesError, setClassesError] = useState(null);
  const [bookingId, setBookingId] = useState(null);       // event id currently being booked
  const [bookedIds, setBookedIds] = useState(new Set());  // event ids successfully booked this session
  const [toast, setToast] = useState(null);               // { kind: 'success' | 'error', text: string }

  // Membership status — drives whether the big membership pricing tiles
  // render at all (members) or appear prominently at the top (non-members).
  // Initial value `null` means "loading" — we don't render either layout
  // until we know, to avoid layout shift.
  const [hasActiveMembership, setHasActiveMembership] = useState(null);

  const fetchClasses = useCallback(async () => {
    setLoadingClasses(true);
    setClassesError(null);
    try {
      // Default range: now → +14 days. Backend defaults match, but explicit here.
      const from = new Date().toISOString();
      const to = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString();
      const data = await api.getTeamupClasses(from, to);
      setClasses(Array.isArray(data?.classes) ? data.classes : []);
    } catch (err) {
      setClassesError(err?.error || 'Could not load the timetable. Pull to retry.');
      setClasses([]);
    } finally {
      setLoadingClasses(false);
    }
  }, []);

  const fetchMembershipStatus = useCallback(async () => {
    try {
      const data = await api.getTeamupMembershipStatus();
      setHasActiveMembership(!!data?.hasActiveMembership);
    } catch (err) {
      // Default to "not a member" so the membership tiles still appear on a
      // transient API failure (better than hiding them from a non-member).
      setHasActiveMembership(false);
    }
  }, []);

  useEffect(() => {
    fetchClasses();
    fetchMembershipStatus();
  }, [fetchClasses, fetchMembershipStatus]);

  // Auto-dismiss toast after 4s
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  async function handleBook(event) {
    // Guests can browse the timetable but must log in to actually book.
    if (!session) { nav('/login'); return; }
    if (bookingId || bookedIds.has(event.id)) return;
    setBookingId(event.id);
    try {
      // Pass the class details so the backend can send a branded confirmation
      // email with a one-tap "Add to Calendar" invite.
      await api.bookTeamupClass(event.id, {
        name: event.name,
        startsAt: event.startsAt,
        endsAt: event.endsAt,
        venue: event.venue,
        instructor: event.instructor,
      });
      setBookedIds(prev => {
        const next = new Set(prev);
        next.add(event.id);
        return next;
      });
      setToast({ kind: 'success', text: `${event.name} booked for ${formatDayLabel(event.startsAt)} ${formatTime(event.startsAt)} — enjoy your class!` });
    } catch (err) {
      setToast({ kind: 'error', text: err?.error || 'Booking failed. Please try again.' });
    } finally {
      setBookingId(null);
    }
  }

  // Group classes by day for the timetable rendering
  const grouped = classes.reduce((acc, cls) => {
    const key = dayKey(cls.startsAt);
    if (!acc[key]) acc[key] = [];
    acc[key].push(cls);
    return acc;
  }, {});
  const dayKeys = Object.keys(grouped);

  return (
    <div className="page">

      {/* Memberships + drop-in — shown at the top only for NON-members.
          Members (hasActiveMembership === true) skip this entirely so the
          page stays focused on the timetable. While loading
          (hasActiveMembership === null) we don't render either way to avoid
          a layout flash. */}
      {hasActiveMembership === false && (
        <>
          <div className="section-title" style={{ marginTop: 4 }}>Gym Memberships</div>
          <p style={{ color: 'var(--grey-700)', fontSize: '0.85rem', marginBottom: 16, lineHeight: 1.5 }}>
            No joining fee. 3-month minimum, then rolling monthly. All memberships include an initial consultation.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16 }}>
            {MEMBERSHIPS.map(m => (
              <div key={m.name} className="card" style={{ overflow: 'hidden' }}>
                <div style={{ background: m.colour, padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    {m.badge && <div style={{ fontSize: '0.62rem', fontWeight: 700, color: 'rgba(255,255,255,0.7)', letterSpacing: '0.1em', marginBottom: 2 }}>{m.badge.toUpperCase()}</div>}
                    <div style={{ color: '#fff', fontWeight: 800, fontSize: '1.05rem' }}>{m.name}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <span style={{ color: '#fff', fontWeight: 900, fontSize: '1.3rem' }}>{m.price}</span>
                    <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.78rem' }}>{m.period}</span>
                  </div>
                </div>
                <div style={{ padding: '14px 18px' }}>
                  {m.features.map(f => (
                    <div key={f} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', fontSize: '0.85rem', color: 'var(--grey-700)' }}>
                      <span style={{ color: m.colour, fontWeight: 700, flexShrink: 0 }}>✓</span>
                      {f}
                    </div>
                  ))}
                  <a href={joinUrl} target="_blank" rel="noopener noreferrer"
                    style={{ display: 'block', marginTop: 14, textAlign: 'center', padding: '10px', borderRadius: 8, background: m.colour, color: '#fff', fontWeight: 700, fontSize: '0.88rem', textDecoration: 'none' }}>
                    Join Now →
                  </a>
                </div>
              </div>
            ))}
          </div>

          {/* Day pass / open gym — single visit for non-members. TeamUp lets
              people without a membership book a class or open gym for £14. */}
          <div className="section-title">No Membership? Pay As You Go</div>
          <div className="card" style={{ overflow: 'hidden', marginBottom: 16 }}>
            <div style={{ background: '#2E7D32', padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: '0.62rem', fontWeight: 700, color: 'rgba(255,255,255,0.7)', letterSpacing: '0.1em', marginBottom: 2 }}>NO MEMBERSHIP NEEDED</div>
                <div style={{ color: '#fff', fontWeight: 800, fontSize: '1.05rem' }}>Day Pass · Open Gym</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <span style={{ color: '#fff', fontWeight: 900, fontSize: '1.3rem' }}>£14</span>
                <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.78rem' }}>/visit</span>
              </div>
            </div>
            <div style={{ padding: '14px 18px' }}>
              {['Open gym access or book into a class', 'All equipment included', 'Pay as you go — no commitment'].map(f => (
                <div key={f} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', fontSize: '0.85rem', color: 'var(--grey-700)' }}>
                  <span style={{ color: '#2E7D32', fontWeight: 700, flexShrink: 0 }}>✓</span>
                  {f}
                </div>
              ))}
              <a href={joinUrl} target="_blank" rel="noopener noreferrer"
                style={{ display: 'block', marginTop: 14, textAlign: 'center', padding: '10px', borderRadius: 8, background: '#2E7D32', color: '#fff', fontWeight: 700, fontSize: '0.88rem', textDecoration: 'none' }}>
                Buy Day Pass →
              </a>
            </div>
          </div>

          <div style={{ background: 'var(--green-pale)', borderRadius: 12, padding: 16, marginBottom: 28, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontWeight: 700, color: 'var(--green)', marginBottom: 2 }}>Not ready to commit?</div>
              <div style={{ fontSize: '0.82rem', color: 'var(--grey-700)' }}>10 Class Pack £119</div>
            </div>
            <a href={joinUrl} target="_blank" rel="noopener noreferrer" className="btn btn-primary" style={{ fontSize: '0.82rem', padding: '10px 14px', flexShrink: 0 }}>
              Buy Pack
            </a>
          </div>
        </>
      )}

      {/* In-app class timetable */}
      <div className="section-title" style={{ marginTop: 4 }}>Book a Class</div>
      <p style={{ color: 'var(--grey-700)', fontSize: '0.85rem', marginBottom: 16, lineHeight: 1.5 }}>
        Upcoming classes for the next 2 weeks. Tap to book straight from the app.
      </p>

      {loadingClasses && (
        <div style={{ textAlign: 'center', padding: 24, color: 'var(--grey-600)' }}>Loading timetable…</div>
      )}

      {!loadingClasses && classesError && (
        <div className="card" style={{ padding: 16, marginBottom: 16, background: '#FFF5F5', borderLeft: '4px solid #D32F2F' }}>
          <div style={{ fontWeight: 700, color: '#D32F2F', marginBottom: 4 }}>Couldn't load classes</div>
          <div style={{ fontSize: '0.85rem', color: 'var(--grey-700)', marginBottom: 12 }}>{classesError}</div>
          <button onClick={fetchClasses} className="btn btn-secondary" style={{ fontSize: '0.82rem', padding: '8px 14px' }}>Retry</button>
        </div>
      )}

      {!loadingClasses && !classesError && classes.length === 0 && (
        <div className="card" style={{ padding: 16, marginBottom: 16, textAlign: 'center', color: 'var(--grey-600)' }}>
          No upcoming classes in the next 2 weeks.
        </div>
      )}

      {!loadingClasses && !classesError && classes.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          {dayKeys.map(day => (
            <div key={day} style={{ marginBottom: 16 }}>
              <div style={{
                fontSize: '0.72rem', fontWeight: 700, color: 'var(--grey-700)',
                letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 8,
              }}>{day}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {grouped[day].map(cls => {
                  const colour = pickClassColour(cls.name);
                  const isBooked = bookedIds.has(cls.id);
                  const isBookingNow = bookingId === cls.id;
                  const isFull = cls.isFull;
                  const disabled = isBooked || isBookingNow || isFull || bookingId !== null;
                  const hasCounts = cls.capacity != null && cls.registered != null;
                  const spacesLeft = hasCounts ? Math.max(0, cls.capacity - cls.registered) : null;
                  const lowSpaces = hasCounts && !isFull && spacesLeft > 0 && spacesLeft <= 3;

                  return (
                    <div key={cls.id} className="card" style={{ display: 'flex', alignItems: 'center', padding: '12px 14px', gap: 12 }}>
                      <ClassPhoto name={cls.name} width={44} height={44} radius={8} fontSize={20} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: '0.92rem' }}>{cls.name}</div>
                        <div style={{ fontSize: '0.78rem', color: 'var(--grey-700)', marginTop: 2 }}>
                          {formatTime(cls.startsAt)}
                          {cls.endsAt ? `–${formatTime(cls.endsAt)}` : ''}
                          {cls.instructor ? ` · ${cls.instructor}` : ''}
                        </div>
                        {cls.venue && (
                          <div style={{ fontSize: '0.72rem', color: 'var(--grey-600)', marginTop: 2 }}>{cls.venue}</div>
                        )}
                        {hasCounts && (
                          <div style={{
                            fontSize: '0.72rem', fontWeight: 600, marginTop: 3,
                            color: (isFull || spacesLeft === 0) ? 'var(--danger)' : lowSpaces ? '#FF6200' : 'var(--grey-600)',
                          }}>
                            {(isFull || spacesLeft === 0)
                              ? 'Class full'
                              : `${cls.registered}/${cls.capacity} booked${lowSpaces ? ` · ${spacesLeft} ${spacesLeft === 1 ? 'space' : 'spaces'} left` : ''}`}
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() => handleBook(cls)}
                        disabled={disabled}
                        style={{
                          flexShrink: 0,
                          padding: '8px 14px',
                          borderRadius: 8,
                          border: 'none',
                          fontWeight: 700,
                          fontSize: '0.82rem',
                          cursor: disabled ? 'default' : 'pointer',
                          background: isBooked ? '#2E7D32' : isFull ? 'var(--grey-300)' : colour,
                          color: isFull && !isBooked ? 'var(--grey-700)' : '#fff',
                          opacity: isBookingNow ? 0.6 : 1,
                          minWidth: 78,
                        }}
                      >
                        {isBooked ? 'Booked ✓' : isFull ? 'Full' : isBookingNow ? '…' : 'Book'}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Toast for booking feedback */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: 80, left: '50%', transform: 'translateX(-50%)',
          background: toast.kind === 'success' ? '#2E7D32' : '#D32F2F',
          color: '#fff', padding: '10px 16px', borderRadius: 8,
          fontSize: '0.85rem', fontWeight: 600, boxShadow: '0 4px 12px rgba(0,0,0,0.18)',
          maxWidth: 'calc(100vw - 32px)', textAlign: 'center', zIndex: 1000,
        }}>
          {toast.text}
        </div>
      )}

      {/* Memberships + drop-in moved to the top, conditionally shown for
          non-members only. Members see only the timetable + Our Classes. */}

      {/* Classes grid */}
      <div className="section-title">Our Classes</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
        {CLASSES.map(c => (
          <div key={c.name} className="card" style={{ padding: 0, overflow: 'hidden', textAlign: 'center' }}>
            <ClassPhoto name={c.name} width="100%" height={68} radius={0} fontSize={26} />
            <div style={{ padding: '8px 6px 10px' }}>
              <div style={{ fontWeight: 700, fontSize: '0.88rem' }}>{c.name}</div>
              <div style={{ fontSize: '0.68rem', fontWeight: 600, color: TYPE_COLOURS[c.type], marginTop: 2, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{c.type}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
